import { Component, Inject, ViewChild } from "@angular/core";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { NmsInputButtonComponent } from "@nms-angular-toolkit/nms-input-button";
import { GeneralLinkModel } from "./link-general-model";
import { Link, LinkType } from "../../../links.model";
import { ANGULARJS_ROOTSCOPE, ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { NmsInputNumberComponent } from "@nms-angular-toolkit/nms-input-number";
import { DeviceLinkModel, EndPortLink, LinkEnds } from "../1-devices/link-device-model";
import { LinkStepModelsUtilsService } from "@nms-ng2/app/modules/topology/links/services/link-step-models-utils";
import { LinkGeneralStepConverter } from "./services/link-general-step-converter";
import { LinkService } from "../../../services/link.service";

/**
 * Componente responsável por renderizar os dados da aba 'Geral' na modal de adicionar Link.
 */
@Component({
    selector: "link-general-step",
    templateUrl: "./link-general-step.component.html",
    styleUrls: ["./link-general-step.component.scss"]
})
export class LinkGeneralStepComponent extends AbstractNmsWizardStep {
    model: GeneralLinkModel;
    @ViewChild("linkName")
    linkName: NmsInputButtonComponent;
    @ViewChild("costField")
    costField: NmsInputNumberComponent;
    @ViewChild("distanceField")
    distanceField: NmsInputNumberComponent;
    @ViewChild("attenuationField")
    attenuationField: NmsInputNumberComponent;
    costErrorMessage: string;
    distanceErrorMessage: string;
    attenuationErrorMessage: string;
    lastGeneratedName: string = "";
    linkEnds: DeviceLinkModel;
    linkTypes: LinkType[] = [];
    isDwDm: boolean;

    constructor(private readonly linkService: LinkService,
                private readonly linkStepModelsUtilsService: LinkStepModelsUtilsService,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
                @Inject(ANGULARJS_ROOTSCOPE) private readonly $rootScope: any,
                private readonly linkGeneralTabConverter: LinkGeneralStepConverter) {
        super();
        this.costErrorMessage = this.translate.instant("links.modal.weight.invalidRange");
        this.distanceErrorMessage = this.translate.instant("links.modal.distance.invalidRange");
        this.attenuationErrorMessage = this.translate.instant("links.modal.attenuation.invalidRange");
        this.model = new GeneralLinkModel();
        this.model.generatedName = true;
    }

    async allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(NmsWizardStepValidationResponse.getDefaultValidationResponse());
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(NmsWizardStepValidationResponse.getDefaultValidationResponse());
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        if (this.isCostMaxValueValid()) {
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        }

        return Promise.reject(new NmsWizardStepValidationResponse(false,
            this.translate.instant("response.error.COST_RANGE_VALIDATOR_ERROR")));
    }

    async finishAction(wizardStepsCacheMap: NmsWizardStepMapData): Promise<void> {
        this.model.name = this.linkName.model || "";
        this.model.weight = this.costField.model;
        this.model.distance = this.distanceField?.model;
        this.model.attenuation = this.attenuationField?.model;

        const link: Link = this.linkStepModelsUtilsService.buildLink(wizardStepsCacheMap);
        try {
            if (link.id) {
                this.linkService.updateLink(link);
            } else {
                this.linkService.saveLink(link);
            }
        } finally {
            this.loadingConfig.emit({ showLoading: false });
        }
    }

    async comingFromPreviousStep(deviceLinkModel: DeviceLinkModel,
                                 wizardStepsCacheMap: NmsWizardStepMapData): Promise<GeneralLinkModel> {
        this.isDwDm = deviceLinkModel.isDwdm;
        const generalLinkModel = wizardStepsCacheMap["GeneralLinkModel"] as GeneralLinkModel || this.model;
        wizardStepsCacheMap["GeneralLinkModel"] = generalLinkModel;
        this.model = this.linkGeneralTabConverter.convert(deviceLinkModel, wizardStepsCacheMap);
        this.model.distance = this.model.distance ?? 0;
        this.model.attenuation = this.model.attenuation ?? 0;
        this.loadLinktypes();
        await this.generateLinkName();

        return Promise.resolve(this.model)
    }

    async goingToPreviousStep(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model)
    }

    changeLinkType() {
        this.costField.model = this.linkTypes.filter(linkType => linkType.id === this.model.linkType)[0].defaultWeight;
    }

    async generateLinkName() {
        this.linkEnds = {
            aEndLink: this.model["aEndLink"],
            zEndLink: this.model["zEndLink"]
        } as DeviceLinkModel;

        if (this.model.generatedName) {
           await this.updateLinkName();
        }
    }

    changeGenerateName() {
        const isChecked = this.model.generatedName;
        if (!isChecked) {
            this.lastGeneratedName = this.model.name;
            this.model.generatedName = false;
        } else {
            this.$rootScope.showDialog({
                message: this.translate.instant("links.modal.generate.name.override.confirm"),
                isConfirm: true
            })
            .then(() => this.updateLinkName())
            .catch(() => {
                this.model.generatedName = false
            });
        }
    }

    async updateLinkName() {
        this.linkService.generateName(this.linkEnds).subscribe((generatedName) => {
            this.linkName.model = generatedName;
            this.lastGeneratedName = generatedName;
            this.loadingConfig.emit({ showLoading: false });
        });
    }

    private loadLinktypes() {
        const aEnd: EndPortLink = this.model.aEndLink;
        const zEnd: EndPortLink = this.model.zEndLink;
        const aEndLink = this.linkStepModelsUtilsService.buildEnd(aEnd, this.isDwDm);
        const zEndLink = this.linkStepModelsUtilsService.buildEnd(zEnd, this.isDwDm);
        const linkEnds = new LinkEnds(aEndLink, zEndLink);

        this.linkService.getLinkTypes(linkEnds).subscribe((links) => {
            this.linkTypes = links;
            if (!this.model.linkType || this.doesLinkTypesContainsOldSelection()) {
                this.model.linkType = this.linkTypes[0].id;
                this.costField.model = this.linkTypes[0].defaultWeight;
            }
        });
    }

    /**
     * Verifica se os link types recebidos do backend ainda contem a última seleção de tipo de link
     * feita pelo usuário. Esta verificação é necessária porque, numa edição, o usuário pode trocar
     * a seleção das portas e o último tipo de link selecionado pode não ser mais compatível com
     * as novas portas.
     */
    private doesLinkTypesContainsOldSelection(): boolean {
        return !this.linkTypes.map(linkType => linkType.id).includes(this.model.linkType);
    }

    /**
     * Foi necessário tratar no front pois em casos que o numero inserido é maior que
     * o valor máximo inteiro, ocorre erro 400 na requisição feita ao back-end,
     * não permitindo que a validação fique a cargo do mesmo.
     */
    private isCostMaxValueValid(): boolean {
        return !(this.costField.inputModel.errors && (this.costField.inputModel.errors.max
            || this.costField.inputModel.errors.min || this.costField.inputModel.errors.required));
    }
}
